import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { useConsentStore, type ConsentFlags } from '../../stores/consentStore';
import { useAuthStore } from '../../stores/authStore';

/**
 * Consent banner (spec §10.6 / §10.7).
 *
 * Renders only when the user has not yet expressed any consent choice
 * (`hasDecided === false`) and we're not on a page that owns the full
 * privacy flow itself (`/login`, `/register`, `/dashboard/privacy`).
 *
 * Accessibility notes:
 *
 * - The banner is a `<section role="region" aria-labelledby="…">` so it
 *   shows up in landmark navigation but doesn't trap focus. Power users
 *   can keep using the page while it's open.
 * - When the "Customize" panel expands, focus moves to the first
 *   toggle (the analytics switch). When the banner unmounts (after any
 *   choice), focus is restored to whichever element was focused before
 *   the banner mounted (tracked via `document.activeElement`).
 * - `prefers-reduced-motion` is honored: we don't animate the slide-in
 *   or the customize expansion when the user has the OS preference set.
 * - The X button is labelled "Close (reject non-essential)" so screen
 *   reader users know exactly what dismissing does (default-deny).
 */
const FLAG_COPY: Array<{
  key: keyof ConsentFlags;
  label: string;
  description: string;
  warning?: boolean;
}> = [
  {
    key: 'analytics',
    label: 'Analytics',
    description:
      'Aggregate usage measurement so we can fix slow pages and broken filters. Never tied to your identity in advertiser reports.',
  },
  {
    key: 'personalization',
    label: 'Personalization',
    description:
      'Uses your saved preferences (preferred retailers, brands, calibers) to rank search results.',
  },
  {
    key: 'behavioralAdvertising',
    label: 'Behavioral advertising',
    description:
      'Allows sponsored placements targeted by your browsing on Round Recon. Shares behavioral signals with advertisers.',
    warning: true,
  },
  {
    key: 'advertiserDataSharing',
    label: 'Advertiser data sharing',
    description:
      'Includes your interactions in aggregated, anonymized reports shared with advertisers. Independent of behavioral advertising.',
    warning: true,
  },
  {
    key: 'marketingEmail',
    label: 'Marketing email',
    description:
      'Occasional product news and feature announcements. Alert emails are separate and always sent regardless of this setting.',
  },
];

const SUPPRESSED_PATHS = new Set<string>(['/login', '/register', '/dashboard/privacy']);

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function ConsentBanner() {
  const location = useLocation();
  const hasDecided = useConsentStore((s) => s.hasDecided);
  const lastSyncError = useConsentStore((s) => s.lastSyncError);
  const acceptAll = useConsentStore((s) => s.acceptAll);
  const rejectAll = useConsentStore((s) => s.rejectAll);
  const acceptOnly = useConsentStore((s) => s.acceptOnly);
  const clearSyncError = useConsentStore((s) => s.clearSyncError);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [customizing, setCustomizing] = useState(false);
  const [draft, setDraft] = useState<ConsentFlags>({
    analytics: false,
    personalization: false,
    behavioralAdvertising: false,
    advertiserDataSharing: false,
    marketingEmail: false,
  });

  const firstToggleRef = useRef<HTMLInputElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  // Capture the element that had focus before the banner mounted so we
  // can restore it on dismiss (per WAI-ARIA APG: focus management on
  // dismissable widgets that don't trap).
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (hasDecided) return;
    if (typeof document !== 'undefined') {
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body) {
        returnFocusRef.current = active;
      }
    }
    return () => {
      const target = returnFocusRef.current;
      if (target && typeof target.focus === 'function' && document.contains(target)) {
        try {
          target.focus();
        } catch {
          /* ignore (element may have been replaced) */
        }
      }
    };
    // Intentionally empty — we want capture-once-on-mount semantics.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user opens the customize panel, focus the first toggle.
  useEffect(() => {
    if (customizing && firstToggleRef.current) {
      firstToggleRef.current.focus();
    }
  }, [customizing]);

  if (hasDecided) return null;
  if (SUPPRESSED_PATHS.has(location.pathname)) return null;

  function handleToggle(key: keyof ConsentFlags) {
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleSaveCustomize() {
    acceptOnly(draft);
  }

  const reducedMotion = prefersReducedMotion();

  return (
    <>
      <Paper
        component="section"
        role="region"
        aria-labelledby="consent-banner-heading"
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.snackbar - 1,
          mx: 'auto',
          maxWidth: { xs: '100%', md: 900 },
          mb: { xs: 0, md: 2 },
          p: { xs: 2, md: 3 },
          borderTopLeftRadius: { xs: 0, md: 8 },
          borderTopRightRadius: { xs: 0, md: 8 },
          borderBottomLeftRadius: { xs: 0, md: 8 },
          borderBottomRightRadius: { xs: 0, md: 8 },
          backgroundColor: 'background.paper',
          boxShadow: 8,
          // Slide up unless prefers-reduced-motion is set. Theme already
          // forces 0.001ms via the global override, but we still skip
          // the keyframe entirely so SR users don't hear "expanded".
          ...(reducedMotion
            ? {}
            : {
                animation: 'consentBannerSlideIn 240ms ease-out',
                '@keyframes consentBannerSlideIn': {
                  from: { transform: 'translateY(100%)' },
                  to: { transform: 'translateY(0)' },
                },
              }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              id="consent-banner-heading"
              variant="h2"
              component="h2"
              ref={headingRef}
              tabIndex={-1}
              sx={{ fontSize: '1.125rem', mb: 1 }}
            >
              We respect your privacy
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Round Recon only collects what we need to help you find ammunition. You can change any
              of these choices later
              {isAuthenticated ? ' from your account settings.' : '.'}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close (reject non-essential)"
            onClick={() => rejectAll()}
            size="small"
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mb: customizing ? 2 : 0 }}
        >
          <Button variant="contained" color="primary" onClick={() => acceptAll()}>
            Accept all
          </Button>
          <Button variant="outlined" color="primary" onClick={() => rejectAll()}>
            Reject non-essential
          </Button>
          <Button
            variant="text"
            color="primary"
            onClick={() => setCustomizing((c) => !c)}
            aria-expanded={customizing}
            aria-controls="consent-customize-panel"
          >
            {customizing ? 'Hide options' : 'Customize'}
          </Button>
        </Stack>

        {customizing ? (
          <Box
            id="consent-customize-panel"
            sx={{
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Stack spacing={2} role="group" aria-label="Customize consent preferences">
              {FLAG_COPY.map((flag, idx) => {
                const inputId = `consent-flag-${flag.key}`;
                const helpId = `${inputId}-help`;
                const checked = draft[flag.key];
                return (
                  <Box key={flag.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          id={inputId}
                          checked={checked}
                          onChange={() => handleToggle(flag.key)}
                          inputRef={idx === 0 ? firstToggleRef : undefined}
                          inputProps={{
                            'aria-describedby': helpId,
                          }}
                        />
                      }
                      label={
                        <Typography component="span" variant="subtitle2">
                          {flag.label}
                        </Typography>
                      }
                    />
                    <Typography
                      id={helpId}
                      variant="body2"
                      color={flag.warning ? 'warning.main' : 'text.secondary'}
                      sx={{ ml: 6 }}
                    >
                      {flag.warning ? 'Heads up: ' : null}
                      {flag.description}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="flex-end"
              sx={{ mt: 2 }}
            >
              <Button variant="text" onClick={() => setCustomizing(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveCustomize}>
                Save preferences
              </Button>
            </Stack>
          </Box>
        ) : null}

        {isAuthenticated ? (
          <Box sx={{ mt: 2 }}>
            <MuiLink component={RouterLink} to="/dashboard/privacy" variant="body2">
              Manage anytime in your account settings
            </MuiLink>
          </Box>
        ) : null}
      </Paper>

      {/* Toast for sync failures. We surface it once, then clear the
          error from the store so subsequent successful syncs don't pop
          the toast again. */}
      <Snackbar
        open={Boolean(lastSyncError)}
        autoHideDuration={6000}
        onClose={() => clearSyncError()}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message="Your privacy choice was saved on this device but couldn't be synced. We'll retry next time you sign in."
        ContentProps={{ role: 'status', 'aria-live': 'polite' }}
      />
    </>
  );
}

export default ConsentBanner;
